import React, { ReactElement, useContext } from 'react';
import { Responsive } from '@wds/responsive';
import { Immutable } from '@wds/utils';
import { getClientEnvironmentDetails } from '@sm/utils';
import { Brand, Session, UserContext } from './types';
import { VARIANTS, VariantKey } from './variants';
import { Actions } from './components/actions';
import { MobileNav } from './components/mobile-nav';
import { NavBar } from './components/nav-bar';
import { Navigation } from './components/navigation';
import { Title } from './components/title';
import { Logo } from './components/logo';

import useStyles from './useStyles';
import { withHeaderTheme } from './theme';

import HeaderContext from './HeaderContext';
import { StaticContext } from '../../..';
import {
  UserContextQueryQuery,
  useUserContextQueryQuery,
} from './UserContext.generated';

export type HeaderProps = Immutable<{
  isEUDC: boolean;
  // TODO: Better define this type
  lang: string;
  variant?: VariantKey;
  title?: string;
  session?: Session;
}>;

const getUserContext = (
  loading: boolean,
  data?: UserContextQueryQuery
): UserContext | undefined => {
  if (loading || !(data && data.user)) return undefined;
  return {
    userId: data.user.id,
    packageId: data.user.package.id,
    packageName: data.user.package.canonicalName,
  };
};

const HeaderContent = ({
  isEUDC,
  lang,
  variant = 'default',
  title = '',
  session,
}: HeaderProps): ReactElement => {
  const classes = useStyles();
  const { HEADER_EXP = {} } = useContext(StaticContext);
  const { isThemeTreatment = false } = HEADER_EXP;
  const themeTreatment: Brand =
    isThemeTreatment || variant === 'mrx' ? 'Momentive' : 'SurveyMonkey';

  const headerVariant = VARIANTS[variant];

  const headerBrand = headerVariant.brand ?? themeTreatment;
  const headerTitle = title || headerVariant.title;
  const { loading, data } = useUserContextQueryQuery();
  const { isNode, isDesktop } = getClientEnvironmentDetails();

  return (
    <HeaderContext.Provider
      value={{
        isEUDC,
        lang,
        session,
        user: getUserContext(loading, data),
        // first segment of the pathname
        pageName: !isNode
          ? document.location.pathname.split('/')?.[1] || ''
          : '',
        pageURL: !isNode ? document.location.pathname : '',
      }}
    >
      <NavBar>
        <MobileNav
          brand={headerBrand}
          title={headerVariant.title}
          navItems={headerVariant.getMenuItems(HEADER_EXP)}
          userMenu={headerVariant.user}
          link={headerVariant.homePage}
          infoMenu={headerVariant.info}
          create={headerVariant.actions.length > 0}
        />
        <Responsive minWidth="md" defaultMatches={isDesktop}>
          <>
            <span className={classes.navSection}>
              <Responsive minWidth="xl">
                <Logo brand={headerBrand} link={headerVariant.homePage} />
              </Responsive>
              <Responsive minWidth="xl">
                <>
                  {headerTitle && <Title>{headerTitle}</Title>}
                  <Navigation items={headerVariant.getMenuItems(HEADER_EXP)} />
                </>
              </Responsive>
            </span>
            <span className={classes.navSection}>
              {headerVariant.actions && (
                <Actions items={headerVariant.actions} />
              )}
              <Responsive minWidth="xl">
                <>{headerVariant.info}</>
              </Responsive>
              {headerVariant.user}
            </span>
          </>
        </Responsive>
      </NavBar>
    </HeaderContext.Provider>
  );
};

const HeaderWithTheme = withHeaderTheme(HeaderContent);

const Header = (props: HeaderProps) => {
  return <HeaderWithTheme theme="elevate" {...props} />;
};

export default Header;
export { Header };
